/**
 * Каталог программ обучения по структуре заказчицы (training-catalog-v2; ТЗ 26.10.2026, п.4.4 и
 * 4.6): дерево разделов, подписи атрибутов программы. Зеркало серверных правил
 * (backend/internal/domain/training_catalog.go) — только для отображения.
 */
import type { EducationBase, Program, TrainingDirection, TrainingDirectionItem } from '@/lib/api'
import { TRAINING_DIRECTION_LABELS } from '@/lib/training'
import type { DocumentTypeKind } from '@/design-system/components/primitives/document-type-badge'

/** Подписи базы — как в каталоге заказчицы. */
export const EDUCATION_BASE_LABELS: Record<EducationBase, string> = {
  any: 'Любое',
  vo_spo: 'Высшее/Среднее профессиональное образование',
  school: 'Школа',
}

/** Глубина дерева разделов (раздел → подраздел → подраздел). */
export const CATALOG_MAX_DEPTH = 3

/** Раздел дерева в порядке обхода: глубина (1 — корень) и путь названий от корня. */
export interface DirectionNode {
  direction: TrainingDirectionItem
  depth: number
  path: string[]
}

/**
 * Разделы в порядке дерева: раздел, затем его подразделы (порядок уровня — `sort`, затем как
 * пришло с сервера). Разделы с потерянным родителем выводятся корнями — ничего не теряется.
 */
export function flattenDirections(directions: TrainingDirectionItem[]): DirectionNode[] {
  const ids = new Set(directions.map((d) => d.id))
  const byParent = new Map<string, TrainingDirectionItem[]>()
  for (const d of directions) {
    const parent = d.parentId && ids.has(d.parentId) ? d.parentId : ''
    const list = byParent.get(parent) ?? []
    list.push(d)
    byParent.set(parent, list)
  }
  const out: DirectionNode[] = []
  const visit = (parentId: string, depth: number, path: string[]) => {
    const children = [...(byParent.get(parentId) ?? [])].sort((a, b) => a.sort - b.sort)
    for (const d of children) {
      const nodePath = [...path, d.name]
      out.push({ direction: d, depth, path: nodePath })
      if (depth < CATALOG_MAX_DEPTH + 1) visit(d.id, depth + 1, nodePath)
    }
  }
  visit('', 1, [])
  return out
}

/** Путь раздела «Охрана труда / Базовые программы» (пусто — раздел не найден). */
export function directionPathLabel(nodes: DirectionNode[], directionId: string): string {
  return nodes.find((n) => n.direction.id === directionId)?.path.join(' / ') ?? ''
}

/** «16 ч». */
export function formatHours(hours: number | null): string {
  return hours == null ? '' : `${hours} ч`
}

/** «4 080 ₽» (неразрывные пробелы, как в каталоге). */
export function formatRubles(kopecks: number | null | undefined): string {
  if (kopecks == null) return ''
  const rub = Math.floor(kopecks / 100)
  const rest = kopecks % 100
  const digits = String(rub).replace(/\B(?=(\d{3})+(?!\d))/g, '\u00a0')
  return `${digits}${rest ? `,${String(rest).padStart(2, '0')}` : ''}\u00a0₽`
}

function pluralYears(n: number): string {
  const mod10 = n % 10
  const mod100 = n % 100
  if (mod10 === 1 && mod100 !== 11) return 'год'
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return 'года'
  return 'лет'
}

/**
 * Периодичность программы: годы — «3 года», не кратная году — «18 мес.»; без числа лет — подпись
 * как в каталоге («Бессрочно», «Определяет работодатель», «-»), а у программ, заведённых до
 * каталога, — подпись режима.
 */
export function periodLabel(p: Pick<Program, 'recurrenceMode' | 'recurrenceMonths' | 'periodText'>): string {
  if (p.recurrenceMode === 'months' && p.recurrenceMonths != null) {
    if (p.recurrenceMonths % 12 === 0) {
      const years = p.recurrenceMonths / 12
      return `${years} ${pluralYears(years)}`
    }
    return `${p.recurrenceMonths} мес.`
  }
  if (p.periodText) return p.periodText
  return p.recurrenceMode === 'employer' ? 'Определяет работодатель' : 'Бессрочно'
}

/**
 * Значение фильтра «Периодичность» каталога (ответ заказчицы 04.11: «Менее года, 1 год и т.д.»):
 * периодичность меньше года — одним вариантом «Менее года», кратная году — «N лет», иначе «N мес.»;
 * без числа — подпись каталога («Бессрочно», «Определяет работодатель»…). `order` — порядок в
 * списке: сначала по возрастанию срока, затем подписи без срока.
 */
export function periodFilterKey(
  p: Pick<Program, 'recurrenceMode' | 'recurrenceMonths' | 'periodText'>,
): { key: string; label: string; order: number } {
  if (p.recurrenceMode === 'months' && p.recurrenceMonths != null && p.recurrenceMonths > 0) {
    if (p.recurrenceMonths < 12) return { key: 'lt12', label: 'Менее года', order: 0 }
    return { key: `m${p.recurrenceMonths}`, label: periodLabel(p), order: p.recurrenceMonths }
  }
  const label = periodInfoLabel(p)
  const text = label === 'не определена' ? 'Не определена' : label
  return { key: `t:${text}`, label: text, order: Number.MAX_SAFE_INTEGER }
}

/** Колонки журнала сроков, которые можно назначить разделу (системные коды направлений). */
export const JOURNAL_COLUMN_OPTIONS: { code: TrainingDirection; label: string }[] = (
  ['ot', 'fire', 'ecology', 'work_skills', 'industrial_safety', 'other'] as TrainingDirection[]
).map((code) => ({ code, label: TRAINING_DIRECTION_LABELS[code] }))

/** Строка программы «Краткое · 16 ч · Документ» (компактные места: состав пакета, выбор замены). */
export function programLine(p: { shortName: string; name: string; hours: number | null; documentType: string }): string {
  return [p.shortName || p.name, formatHours(p.hours), p.documentType].filter(Boolean).join(' · ')
}

/** Дата добавления в каталог «28.10.2026». */
export function formatCatalogDate(iso: string | undefined): string {
  if (!iso) return ''
  const d = new Date(iso)
  return Number.isNaN(d.getTime())
    ? ''
    : d.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' })
}

/**
 * Вид итогового документа программы — плашка перед названием (комментарий заказчицы к ТЗ, п.4.5):
 * по первому слову значения каталога. «Свидетельство о профессии рабочего…» и «Свидетельство» —
 * один вид; удостоверения о ПК, о допуске к работам в ОЗП и на высоте — один вид.
 */
export type DocumentKind = DocumentTypeKind

export const DOCUMENT_KIND_LABELS: Record<DocumentKind, string> = {
  cert: 'Удостоверение',
  diploma: 'Диплом',
  protocol: 'Протокол',
  svid: 'Свидетельство',
}

export function documentKind(documentType: string): DocumentKind | null {
  const first = documentType.trim().split(/\s+/)[0]?.toLowerCase() ?? ''
  if (first.startsWith('удостоверени')) return 'cert'
  if (first.startsWith('диплом')) return 'diploma'
  if (first.startsWith('протокол')) return 'protocol'
  if (first.startsWith('свидетельств')) return 'svid'
  return null
}

/**
 * Полное название документа (ответ на вопрос 15): сокращения каталога раскрываются — «о ПК» → «о
 * повышении квалификации», «о ПП» → «о профессиональной переподготовке».
 */
export function documentFullName(documentType: string): string {
  return documentType
    .trim()
    .replace(/(^|\s)о ПК$/u, '$1о повышении квалификации')
    .replace(/(^|\s)о ПП$/u, '$1о профессиональной переподготовке')
}

/** Базы для «Инфо» — полностью, строчными (ТЗ 4.3: «База без сокращения»). */
export const EDUCATION_BASE_INFO: Record<EducationBase, string> = {
  any: 'любое образование',
  vo_spo: 'высшее/среднее профессиональное образование',
  school: 'школа',
}

/** Периодичность для «Инфо»: как в каталоге; «-» — «не определена» (ответ заказчицы 27.10, В4). */
export function periodInfoLabel(p: Pick<Program, 'recurrenceMode' | 'recurrenceMonths' | 'periodText'>): string {
  const label = periodLabel(p)
  return label.trim() === '-' ? 'не определена' : label
}

/** «-» и пусто в каталоге — значения нет (разряд, профстандарт не показываются). */
export function catalogValue(v: string | undefined | null): string {
  const t = (v ?? '').trim()
  return t === '-' ? '' : t
}

/** Нормализация для поиска: регистр и «ё» не важны. */
export function searchKey(s: string): string {
  return s.toLowerCase().replaceAll('ё', 'е')
}

/**
 * Совпадение программы с поиском (от 2 символов): краткое и полное наименование, код курса,
 * «Подлежат обучению» (найдёт «сварщик» по аудитории).
 */
export function programMatches(
  p: Pick<Program, 'name' | 'shortName' | 'code' | 'audience'>,
  query: string,
): boolean {
  const q = searchKey(query.trim())
  if (q.length < 2) return true
  return [p.shortName, p.name, p.code, ...p.audience].some((v) => searchKey(v ?? '').includes(q))
}
